import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Sparkles,
  FolderOpen,
  Settings,
  LogOut,
  BookOpen,
  Share2,
  Star,
  Bookmark,
  HelpCircle,
  Trash2,
  Shield,
  KeyRound,
  Bot
} from 'lucide-react';
import BrandMark from './BrandMark';

function AppShell({ children, onLogout, user }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.body.classList.add('app-shell-active');
    return () => {
      document.body.classList.remove('app-shell-active');
    };
  }, []);

  const isAdminAccount = useMemo(() => {
    const level = String(user?.account_level || user?.account_tier || user?.role || '').trim().toLowerCase();
    return Boolean(user?.is_admin) || level === 'admin';
  }, [user]);

  const navItems = [
    { to: '/prompts/create', label: 'Create Prompt', icon: Sparkles, permission: 'page_create_prompt' },
    {
      to: '/agent-instructions',
      label: 'Agent Instructions',
      icon: Bot,
      permission: 'page_agent_instructions',
      disabled: true,
      badge: 'Coming Soon'
    },
    { to: '/projects', label: 'Projects', icon: FolderOpen, permission: 'page_projects' },
    { to: '/library', label: 'Prompt Library', icon: BookOpen, permission: 'page_prompt_library' },
    { to: '/favorites', label: 'Favorite Prompts', icon: Star, permission: 'page_favorites' },
    { to: '/saved', label: 'Saved Prompts', icon: Bookmark, permission: 'page_saved_prompts' },
    { to: '/shared', label: 'Shared', icon: Share2, permission: 'page_shared' },
    { to: '/help', label: 'Help', icon: HelpCircle, permission: 'page_help' },
    { to: '/trash', label: 'Trash', icon: Trash2, permission: 'page_trash' },
    { to: '/api', label: 'API', icon: KeyRound, permission: 'page_api' },
    { to: '/admin', label: 'Admin', icon: Shield, permission: 'page_admin' },
    { to: '/settings', label: 'Settings', icon: Settings, permission: 'page_settings' },
  ];

  const initials = useMemo(() => {
    const fallback = 'RP';
    if (!user?.name && !user?.email) return fallback;
    const source = user?.name || user?.email || '';
    const parts = source.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return fallback;
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }, [user]);

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.to;
    const baseClass = `side-link ${isActive ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`;
    const content = (
      <>
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        {item.badge ? <span className="side-link-badge">{item.badge}</span> : null}
      </>
    );

    if (item.disabled) {
      return (
        <div className={baseClass} data-tooltip="Coming soon" aria-label={`${item.label} coming soon`}>
          {content}
        </div>
      );
    }

    return (
      <Link to={item.to} className={baseClass} onClick={() => setMobileOpen(false)}>
        {content}
      </Link>
    );
  };

  const permissions = user?.permissions || null;
  const hasAccess = (permissionKey) => {
    if (!permissionKey) return true;
    if (permissionKey === 'page_agent_instructions') return true;
    if (permissionKey === 'page_admin') return isAdminAccount;
    if (!permissions) return true;
    if (!(permissionKey in permissions)) return true;
    return Boolean(permissions[permissionKey]);
  };
  const filteredNavItems = navItems.filter((item) => hasAccess(item.permission));

  return (
    <div className="app-shell">
      <aside className="side-nav">
        <div className="brand-block">
          <BrandMark />
          <div className="brand-text">
            <div className="brand-title">Rebel Prompts</div>
            <div className="brand-subtitle">Prompt OS</div>
          </div>
        </div>

        <Link className="profile-block" to="/profile" onClick={() => setMobileOpen(false)}>
          <div className="profile-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user?.name || 'User'} />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="profile-meta">
            <div className="profile-name">
              {user?.name || 'Rebel User'}
              {isAdminAccount && (
                <>
                  <Shield className="w-4 h-4 admin-badge" />
                  <span className="admin-pill">Admin</span>
                </>
              )}
            </div>
            <div className="profile-sub">Account</div>
          </div>
        </Link>

        <nav className="side-links">
          {filteredNavItems.map((item) => (
            <NavLink key={item.to} item={item} />
          ))}
        </nav>

        <button className="side-link logout" onClick={onLogout}>
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </aside>

      <div className="mobile-topbar">
        <button className="icon-btn" onClick={() => setMobileOpen(true)}>
          <Menu className="w-6 h-6" />
        </button>
        <div className="mobile-brand">
          <BrandMark size={30} />
          <span>Rebel Prompts</span>
        </div>
      </div>

      <div className={`mobile-drawer ${mobileOpen ? 'open' : ''}`}>
        <div className="drawer-header">
          <div className="brand-block">
            <BrandMark />
            <div className="brand-text">
              <div className="brand-title">Rebel Prompts</div>
              <div className="brand-subtitle">Prompt OS</div>
            </div>
          </div>
          <button className="icon-btn" onClick={() => setMobileOpen(false)}>
            <X className="w-6 h-6" />
          </button>
        </div>
        <Link className="profile-block mobile" to="/profile" onClick={() => setMobileOpen(false)}>
          <div className="profile-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user?.name || 'User'} />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="profile-meta">
            <div className="profile-name">
              {user?.name || 'Rebel User'}
              {isAdminAccount && (
                <>
                  <Shield className="w-4 h-4 admin-badge" />
                  <span className="admin-pill">Admin</span>
                </>
              )}
            </div>
            <div className="profile-sub">Account</div>
          </div>
        </Link>
        <nav className="side-links">
          {filteredNavItems.map((item) => (
            <NavLink key={item.to} item={item} />
          ))}
        </nav>
        <button className="side-link logout" onClick={onLogout}>
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
      {mobileOpen && <div className="drawer-backdrop" onClick={() => setMobileOpen(false)} />}

      <main className="app-main">{children}</main>
    </div>
  );
}

export default AppShell;
